import React, {useMemo, useState} from "react";
import {
    Accordion, AccordionDetails, AccordionSummary,
    Box, Chip, Pagination, Paper, Stack,
    Table, TableBody, TableCell, TableHead, TableRow, Typography
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {RecordQueryPageinationResponse} from "../data/RecordQueryPageinationResponse";
import {RecordQueryLogResponse} from "../data/RecordQueryLogResponse";
import {formatTimestamp} from "../utils";
import {PoliceUser} from "../components/PoliceUser";
import {
    RecordLogFilterPanel,
    RecordLogFilterState,
    emptyRecordLogFilters,
    buildRecordLogSearchUrl,
} from "../components/records/RecordLogFilterPanel";
import useApiCall from "../hooks/CancellableApiCall";
import {LoadingContent} from "../components/LoadingContent";

interface SessionGroup {
    sessionId: string | null
    supervisorCommunityId: string
    supervisorNick: string
    employeeCommunityId: string
    employeeNick: string
    firstDate: string
    reason: string
    entries: RecordQueryLogResponse[]
}

function groupBySession(logs: RecordQueryLogResponse[]): SessionGroup[] {
    const sessionMap = new Map<string, RecordQueryLogResponse[]>()
    const ungrouped: RecordQueryLogResponse[] = []

    for (const log of logs) {
        if (log.sessionId) {
            const existing = sessionMap.get(log.sessionId) ?? []
            existing.push(log)
            sessionMap.set(log.sessionId, existing)
        } else {
            ungrouped.push(log)
        }
    }

    const groups: SessionGroup[] = []

    sessionMap.forEach((entries, sessionId) => {
        const searchEntry = entries.find(e => e.accessType === "Record Search")
        const first = searchEntry ?? entries[0]
        groups.push({
            sessionId,
            supervisorCommunityId: first.supervisorCommunityId,
            supervisorNick: first.nick,
            employeeCommunityId: first.employeeCommunityId,
            employeeNick: first.employeeNick,
            firstDate: first.date,
            reason: searchEntry?.reason ?? "",
            entries: entries.sort((a, b) => a.id - b.id),
        })
    })

    for (const log of ungrouped) {
        groups.push({
            sessionId: null,
            supervisorCommunityId: log.supervisorCommunityId,
            supervisorNick: log.nick,
            employeeCommunityId: log.employeeCommunityId,
            employeeNick: log.employeeNick,
            firstDate: log.date,
            reason: log.reason,
            entries: [log],
        })
    }

    groups.sort((a, b) => new Date(b.firstDate).getTime() - new Date(a.firstDate).getTime())
    return groups
}

function accessTypeColor(type: string): "default" | "primary" | "secondary" | "warning" | "info" | "success" {
    switch (type) {
        case "Record Search": return "primary"
        case "Infraction View": return "warning"
        case "Notes View": return "info"
        case "Complaints View": return "secondary"
        case "Inbox View": return "success"
        case "Inbox Open": return "success"
        default: return "default"
    }
}

export const RecordQueryLogPage = () => {
    const [page, setPage] = useState(1)
    const [filters, setFilters] = useState<RecordLogFilterState>(emptyRecordLogFilters)

    const searchUrl = useMemo(() => buildRecordLogSearchUrl(filters, page), [filters, page])
    const logsData = useApiCall<RecordQueryPageinationResponse>({url: searchUrl})

    const handleChangePage = (_: React.ChangeEvent<unknown>, value: number) => setPage(value)
    const handleFiltersChange = (f: RecordLogFilterState) => { setFilters(f); setPage(1) }
    const handleFiltersClear = () => { setFilters(emptyRecordLogFilters); setPage(1) }

    const totalPages = Math.max(1, logsData.data?.totalPages ?? 1)
    const sessionGroups = useMemo(() => groupBySession(logsData.data?.logs ?? []), [logsData.data])

    return <Stack spacing={2}>
        <Paper variant="outlined" sx={{p: 2}}>
            <RecordLogFilterPanel
                filters={filters}
                onChange={handleFiltersChange}
                onClear={handleFiltersClear}
            />
        </Paper>

        <Paper variant="outlined" sx={{p: 2, overflow: "hidden"}}>
            <LoadingContent showLoadingSkeleton isLoading={logsData.isLoading}>
                <Stack spacing={1}>
                    {sessionGroups.length === 0 && (
                        <Typography variant="body2" color="text.secondary" align="center" sx={{py: 4}}>
                            No records found
                        </Typography>
                    )}
                    {sessionGroups.map((group, idx) => (
                        group.entries.length > 1 ? (
                            <Accordion key={group.sessionId ?? idx} variant="outlined" disableGutters>
                                <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{width: "100%", pr: 1, flexWrap: "wrap", gap: 0.5}}>
                                        <PoliceUser user={{communityId: group.supervisorCommunityId, nick: group.supervisorNick}}/>
                                        <Typography variant="body2" color="text.secondary">→</Typography>
                                        <PoliceUser user={{communityId: group.employeeCommunityId, nick: group.employeeNick}}/>
                                        <Stack direction="row" spacing={0.5} sx={{flexWrap: "wrap", gap: 0.5}}>
                                            {Array.from(new Set(group.entries.map(e => e.accessType))).map(type => (
                                                <Chip key={type} label={type} size="small" color={accessTypeColor(type)} variant="outlined"/>
                                            ))}
                                        </Stack>
                                        {group.reason && (
                                            <Typography variant="body2" color="text.secondary" noWrap sx={{flex: 1, minWidth: 0, display: {xs: "none", sm: "block"}}}>
                                                {group.reason}
                                            </Typography>
                                        )}
                                        <Typography variant="caption" color="text.secondary" sx={{whiteSpace: "nowrap", ml: "auto"}}>
                                            {formatTimestamp(group.firstDate)}
                                        </Typography>
                                    </Stack>
                                </AccordionSummary>
                                <AccordionDetails sx={{pt: 0, overflowX: "auto"}}>
                                    {group.reason && (
                                        <Typography variant="body2" sx={{mb: 1}}>
                                            <strong>Reason:</strong> {group.reason}
                                        </Typography>
                                    )}
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Action</TableCell>
                                                <TableCell>Detail</TableCell>
                                                <TableCell>Time</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {group.entries.map(entry => (
                                                <TableRow key={entry.id}>
                                                    <TableCell>
                                                        <Chip label={entry.accessType} size="small" color={accessTypeColor(entry.accessType)} variant="filled"/>
                                                    </TableCell>
                                                    <TableCell>{entry.reason || "—"}</TableCell>
                                                    <TableCell sx={{whiteSpace: "nowrap"}}>{formatTimestamp(entry.date)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </AccordionDetails>
                            </Accordion>
                        ) : (
                            <Paper key={group.sessionId ?? idx} variant="outlined" sx={{p: 1.5}}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap: "wrap", gap: 0.5}}>
                                    <PoliceUser user={{communityId: group.supervisorCommunityId, nick: group.supervisorNick}}/>
                                    <Typography variant="body2" color="text.secondary">→</Typography>
                                    <PoliceUser user={{communityId: group.employeeCommunityId, nick: group.employeeNick}}/>
                                    <Chip label={group.entries[0].accessType} size="small" color={accessTypeColor(group.entries[0].accessType)} variant="filled"/>
                                    {group.reason && (
                                        <Typography variant="body2" color="text.secondary" sx={{flex: 1, minWidth: 0, display: {xs: "none", sm: "block"}}} noWrap>
                                            {group.reason}
                                        </Typography>
                                    )}
                                    <Typography variant="caption" color="text.secondary" sx={{whiteSpace: "nowrap", ml: "auto"}}>
                                        {formatTimestamp(group.firstDate)}
                                    </Typography>
                                </Stack>
                            </Paper>
                        )
                    ))}
                </Stack>
            </LoadingContent>
            <Box sx={{justifyContent: "center", display: "flex", width: "100%", mt: 2}}>
                <Pagination color="secondary" count={totalPages} page={page} onChange={handleChangePage}/>
            </Box>
        </Paper>
    </Stack>
}
